import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { IoSparkles, IoAdd, IoChevronForward, IoTrashOutline, IoCreateOutline, IoEyeOffOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { listPatchNotes, createPatchNote, deletePatchNote, markPatchesSeen } from "@/lib/api";

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
}

const resolveImg = (v) => (!v ? "" : v);

export default function Novelties() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const { data: list = [], isLoading, refetch } = useQuery({ queryKey: ["patchnotes"], queryFn: listPatchNotes });

  useEffect(() => { markPatchesSeen().catch(() => {}); }, []);

  const createNew = async () => {
    setBusy(true);
    try {
      const p = await createPatchNote({ title: "Nouvelle nouveauté", summary: "", blocks: [], published: false });
      toast.success("Patch créé — à toi de le remplir");
      nav(`/nouveautes/${p.id}?edit=1`);
    } catch { toast.error("Impossible de créer le patch"); } finally { setBusy(false); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette nouveauté ?")) return;
    try { await deletePatchNote(id); toast.success("Supprimé"); refetch(); } catch { toast.error("Échec de la suppression"); }
  };

  return (
    <DojoShell crumbs={[{ label: "Nouveautés" }]}>
      <div style={s.hero}>
        <div style={s.heroGlow} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={s.eyebrow}><IoSparkles /> JOURNAL DU DŌJŌ</div>
          <h1 style={s.h1}>Nouveautés</h1>
          <p style={s.sub}>Chaque mise à jour du Dōjō, racontée. Clique sur un patch pour tout découvrir.</p>
        </div>
        {isAdmin && (
          <button style={{ ...s.newBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="patch-new" onClick={createNew}>
            <IoAdd size={18} /> Nouveau patch
          </button>
        )}
      </div>

      {isLoading && <p style={{ color: "var(--text-secondary)" }}>Chargement…</p>}
      {!isLoading && list.length === 0 && (
        <div style={s.empty} data-testid="patch-empty">
          <span style={{ fontSize: 42 }}>🗞️</span>
          <p style={{ margin: "10px 0 0", color: "var(--text-secondary)" }}>Aucune nouveauté pour l'instant. Reviens vite !</p>
        </div>
      )}

      <div style={s.timeline} data-testid="patch-list">
        {list.map((p) => {
          const cover = resolveImg(p.cover);
          return (
            <div key={p.id} role="button" tabIndex={0} data-testid={`patch-${p.id}`} style={s.card} onClick={() => nav(`/nouveautes/${p.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") nav(`/nouveautes/${p.id}`); }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = RED; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = "var(--border-c)"; }}>
              {cover
                ? <div style={{ ...s.cover, backgroundImage: `url(${cover})` }} />
                : <div style={s.coverFallback}><IoSparkles size={30} /></div>}
              <div style={s.cardBody}>
                <div style={s.cardTop}>
                  <span style={s.date}>{fmt(p.date || p.created_at)}</span>
                  {!p.published && <span style={s.draftTag}><IoEyeOffOutline size={12} /> Brouillon</span>}
                </div>
                <h2 style={s.cardTitle} data-testid={`patch-title-${p.id}`}>{p.title}</h2>
                {p.summary && <p style={s.cardSummary}>{p.summary}</p>}
                <div style={s.cardFoot}>
                  <span style={s.read}>Découvrir <IoChevronForward size={13} /></span>
                  {isAdmin && (
                    <span style={{ display: "flex", gap: 8 }}>
                      <span role="button" tabIndex={0} data-testid={`patch-edit-${p.id}`} style={s.iconBtn} onClick={(e) => { e.stopPropagation(); nav(`/nouveautes/${p.id}?edit=1`); }}><IoCreateOutline size={16} /></span>
                      <span role="button" tabIndex={0} data-testid={`patch-del-${p.id}`} style={{ ...s.iconBtn, color: RED }} onClick={(e) => remove(e, p.id)}><IoTrashOutline size={16} /></span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DojoShell>
  );
}

const s = {
  hero: { position: "relative", overflow: "hidden", display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap", padding: "28px 26px", borderRadius: 22, marginBottom: 26, border: "1px solid color-mix(in srgb, var(--brand) 30%, var(--border-c))", background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 14%, var(--bg-secondary)), var(--bg-secondary))" },
  heroGlow: { position: "absolute", top: -80, right: -40, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 30%, transparent), transparent 70%)" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800, letterSpacing: 2, color: RED },
  h1: { fontSize: 42, fontWeight: 900, margin: "8px 0 6px", fontFamily: "var(--font-display)", color: "var(--text-primary)" },
  sub: { fontSize: 15, color: "var(--text-secondary)", maxWidth: 560, lineHeight: 1.55, margin: 0 },
  newBtn: { position: "relative", zIndex: 1, marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7, padding: "12px 20px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", whiteSpace: "nowrap" },
  empty: { textAlign: "center", padding: "60px 20px", borderRadius: 18, border: "1px dashed var(--border-c)", background: "var(--bg-secondary)" },
  timeline: { display: "flex", flexDirection: "column", gap: 16 },
  card: { position: "relative", display: "flex", gap: 0, textAlign: "left", padding: 0, overflow: "hidden", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", cursor: "pointer", transition: "transform .2s ease, border-color .2s ease", boxShadow: "var(--surface-shadow)" },
  cover: { width: 160, flexShrink: 0, backgroundSize: "cover", backgroundPosition: "center", minHeight: 140 },
  coverFallback: { width: 160, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 22%, var(--bg-tertiary)), var(--bg-tertiary))", color: RED, minHeight: 140 },
  cardBody: { flex: 1, minWidth: 0, padding: "18px 20px", display: "flex", flexDirection: "column" },
  cardTop: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 },
  date: { fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: RED, textTransform: "uppercase" },
  draftTag: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-secondary)" },
  cardTitle: { fontSize: 22, fontWeight: 900, margin: "0 0 6px", fontFamily: "var(--font-display)", color: "var(--text-primary)", lineHeight: 1.15 },
  cardSummary: { fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  cardFoot: { marginTop: "auto", paddingTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between" },
  read: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 800, color: "var(--text-primary)" },
  iconBtn: { width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-secondary)", cursor: "pointer" },
};
